import { useEffect, useState } from 'react'
import { useWhatIfNewLoad } from '../../hooks/useEngineering.ts'
import { LOADING_COLORS, VDROP_COLORS } from '../../types/engineering.ts'
import { minReachableVoltagePu } from '../../utils/voltageDrop.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { friendlyError } from '../../utils/friendlyError.ts'

interface WhatIfPanelProps {
  feederId: number
  transformers: { id: number; name: string | null }[]
  /** Per-meter avg demand assumption (the dashboard slider) — drives the
   *  baseline so it matches the voltage-drop card above. */
  avgDemandKw: number
}

function fmtPct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`
}

function deltaLabel(delta: number, digits = 2): string {
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(digits)}`
}

export function WhatIfPanel({ feederId, transformers, avgDemandKw }: WhatIfPanelProps) {
  const [transformerId, setTransformerId] = useState<number | ''>('')
  const [additionalKw, setAdditionalKw] = useState<string>('50')
  const mutation = useWhatIfNewLoad(feederId)
  const { mutate, data, isPending, isError, error, reset } = mutation

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (transformerId === '') return
    mutate({
      transformer_id: Number(transformerId),
      additional_kw: Number(additionalKw) || 0,
      avg_demand_kw: avgDemandKw,
    })
  }

  // Reset the prior result when ANY input changes (transformer, additional kW,
  // or the dashboard's avg-demand assumption) — a stale result must never
  // render against inputs it did not use (spec §3.5 / §9).
  function onTransformerChange(value: string) {
    setTransformerId(value ? Number(value) : '')
    reset()
  }
  function onAdditionalKwChange(value: string) {
    setAdditionalKw(value)
    reset()
  }
  useEffect(() => {
    reset()
  }, [avgDemandKw, reset])

  const impact = data?.transformer_impact
  // I1: run_what_if_new_load reuses calculate_voltage_drop, and load_for()
  // is only called after placement succeeds, so an unplaceable target
  // transformer's scenario load is silently discarded — scenario ends up
  // identical to baseline with no on-screen reason. Look the target row up
  // in the SCENARIO drops by the panel's selected transformer id; reset()
  // fires on every input change (below), so whenever `data` is populated
  // `transformerId` is still the id that produced it.
  const targetDrop = data?.scenario.transformer_drops.find(
    d => d.transformer_id === transformerId,
  )
  const targetUnplaceable = targetDrop?.placement_status === 'unplaceable'
  // Islanded rows carry 0.0 pu artifacts — exclude them from the min
  // (spec §3.5): minReachableVoltagePu filters source_reachable/islanded.
  const baselineMin = data
    ? minReachableVoltagePu(data.baseline.transformer_drops)
    : null
  const scenarioMin = data
    ? minReachableVoltagePu(data.scenario.transformer_drops)
    : null
  const violationDelta = data
    ? data.scenario.violation_count - data.baseline.violation_count
    : 0
  const maxDropDelta = data
    ? data.scenario.max_drop_pct - data.baseline.max_drop_pct
    : 0

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">What-If: New Load</h3>
        <span className="text-xs text-gray-500">Hypothetical — not saved</span>
      </div>
      {data && <AsOfStamp computedAt={null} />}

      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Transformer</label>
          <select
            value={transformerId}
            onChange={e => onTransformerChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Choose a transformer --</option>
            {transformers.map(t => (
              <option key={t.id} value={t.id}>
                {t.name || `#${t.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500">Additional kW</label>
          <input
            type="number"
            step="any"
            value={additionalKw}
            onChange={e => onAdditionalKwChange(e.target.value)}
            className="w-28 text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <button
          type="submit"
          disabled={transformerId === '' || isPending}
          className="text-sm px-4 py-1.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Running...' : 'Run'}
        </button>
      </form>

      <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        Negative kW models load removal. Voltage figures use the Tier 2 approximation.
      </div>

      {isError && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          {friendlyError(error, 'run the what-if analysis')}
        </div>
      )}

      {data && impact && (
        <div className="space-y-3">
          {/* I1: the target transformer wasn't placed on the feeder's
              conductors, so its new load never entered the scenario model —
              say so instead of silently rendering "no change". */}
          {targetUnplaceable && (
            <div className="text-xs text-gray-700 bg-gray-50 border border-gray-300 rounded px-2 py-1">
              This transformer is not placed on the feeder&rsquo;s conductors
              (more than 100 ft from any segment); the scenario load was not
              modelled.
            </div>
          )}

          {/* Feeder voltage impact */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Feeder Voltage
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white border border-gray-200 rounded p-2 text-center">
                <div className="text-sm font-medium text-gray-700">
                  {baselineMin !== null ? baselineMin.toFixed(4) : '—'}
                  {' '}&rarr;{' '}
                  <span style={{ color: scenarioMin !== null && scenarioMin < 0.95 ? VDROP_COLORS.violation : undefined }}>
                    {scenarioMin !== null ? scenarioMin.toFixed(4) : '—'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">Min Voltage p.u.</div>
              </div>
              <div className="bg-white border border-gray-200 rounded p-2 text-center">
                <div
                  className="text-sm font-medium"
                  style={{ color: maxDropDelta > 0 ? VDROP_COLORS.violation : VDROP_COLORS.normal }}
                >
                  {data.baseline.max_drop_pct.toFixed(2)}%
                  {' '}&rarr;{' '}
                  {data.scenario.max_drop_pct.toFixed(2)}%
                </div>
                <div className="text-xs text-gray-500">
                  Max Drop ({deltaLabel(maxDropDelta)} pts)
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded p-2 text-center">
                <div
                  className="text-lg font-bold"
                  style={{ color: violationDelta > 0 ? VDROP_COLORS.violation : 'inherit' }}
                >
                  {data.baseline.violation_count} &rarr; {data.scenario.violation_count}
                </div>
                <div className="text-xs text-gray-500">
                  Violations ({deltaLabel(violationDelta, 0)})
                </div>
              </div>
            </div>
          </div>

          {/* Transformer loading impact */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Transformer Loading — {impact.transformer_name || `#${impact.transformer_id}`}
            </h4>
            <div className="bg-white border border-gray-200 rounded p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">
                  {impact.kva_rating ? `${impact.kva_rating} kVA` : 'No rating'}
                </span>
                <span className="text-gray-500">
                  {impact.baseline_kw.toFixed(1)} kW &rarr; {impact.scenario_kw.toFixed(1)} kW
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-700">
                    {fmtPct(impact.baseline_loading_pct)}
                  </span>
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: LOADING_COLORS[impact.baseline_status] + '20',
                      color: LOADING_COLORS[impact.baseline_status],
                    }}
                  >
                    {impact.baseline_status}
                  </span>
                </div>
                <span className="text-gray-400">&rarr;</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-700">
                    {fmtPct(impact.scenario_loading_pct)}
                  </span>
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
                    style={{
                      backgroundColor: LOADING_COLORS[impact.scenario_status] + '20',
                      color: LOADING_COLORS[impact.scenario_status],
                    }}
                  >
                    {impact.scenario_status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
